import React, { useMemo } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import TradingFlameIcon from '@wix/da-ds/pkg/Icons/16x16/TradingFlame';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { parseIsoDateOrTimestamp } from '@wix/da-i18next/pkg/time/functions';
import { getHasBeenTraded } from '../../../../types/deviation/getters';
import { getIsAdoptableOfferType } from '../../../../utils/adoptables';
import s from './AdoptableCardStats.scss';

/**
 * Tells us if an adoptable has never entered its offer window
 */
export function getIsBeforeOfferWindow(deviation: PapiDeviation) {
  const { offers = {}, thumbSaleState } = deviation.adoptable ?? {};
  const { offerEndTime } = offers;

  // thumbSaleState is specific to shop thumbs.
  // TODO: If we use this function outside of thumbs, we need to talk to backend about an alternative.
  return offerEndTime === undefined && thumbSaleState === 'open';
}

interface Props {
  deviation: PapiDeviation;
}

const AdoptableCardStats: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();

  const isOfferType = getIsAdoptableOfferType(deviation);
  const { numOffers, offerEndTime } = deviation.adoptable?.offers || {};
  const thumbSaleState = deviation.adoptable?.thumbSaleState || '';
  const hasBeenTraded = getHasBeenTraded(deviation);
  const toDate = parseIsoDateOrTimestamp(offerEndTime);

  // We want time-left to be the number of full hours remaining, until we
  // reach the last hour; then we want full _and partial_ minutes so we
  // don't show Time's Up early
  const hoursLeft = useMemo(() => {
    const MS_PER_HOUR = 1000 * 3600;
    return Math.floor((toDate.getTime() - Date.now()) / MS_PER_HOUR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minsLeft = useMemo(() => {
    const MS_PER_MIN = 1000 * 60;
    return Math.ceil((toDate.getTime() - Date.now()) / MS_PER_MIN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (['sold', 'unlisted', 'expired'].includes(thumbSaleState)) {
    return null;
  }

  // We'll show a CTA if this is adoptable is open to offers and the offer window hasn't started yet
  if (isOfferType && getIsBeforeOfferWindow(deviation)) {
    if (hasBeenTraded) {
      return (
        <div className={c(s['root'])}>
          <div className={s['traded']}>
            <TradingFlameIcon className={s['icon']} size={IconSize.SMALLER} />
            <span>{t('shop.thumb.adoptable.traded')}</span>
          </div>
        </div>
      );
    }

    return (
      <div className={s['root']}>
        <span>{t('shop.thumb.adoptable.cta')}</span>
      </div>
    );
  }

  const timesUp = toDate.getTime() < Date.now();

  return (
    <div className={c(s['root'])}>
      {minsLeft >= 60 && (
        <span>
          {t('shop.thumb.adoptable.hoursLeft', {
            count: hoursLeft,
          })}
        </span>
      )}
      {minsLeft < 60 && minsLeft > 0 && (
        <span className={s['warning']}>
          {t('shop.thumb.adoptable.minsLeft', {
            count: minsLeft,
          })}
        </span>
      )}
      {timesUp && (
        <span className={s['warning']}>
          {t('shop.thumb.adoptable.timesUp')}
        </span>
      )}
      {!!numOffers && (
        <span>
          {t('common.offerCount', {
            count: numOffers,
          })}
        </span>
      )}
      {hasBeenTraded && (
        <div className={s['traded']}>
          <TradingFlameIcon className={s['icon']} size={IconSize.SMALLER} />
          <span>{t('shop.thumb.adoptable.traded')}</span>
        </div>
      )}
    </div>
  );
};
AdoptableCardStats.displayName = 'AdoptableCardStats';

export default AdoptableCardStats;
